import React, { FC, useEffect, useState } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { BackBtn, SearchInput, WarningCard } from 'common/components';
import { IGoodsShort } from 'common/models';
import { Bin, Download, Expand, Options, Plus, Sync } from 'common/svg';
import { DEFAULT_VALUE_0, LIST_LIMIT_20 } from 'common/config';
import { showSuccessMessage } from 'common/helpers';
import { AuthHooks, UserHooks } from 'common/hooks';
import { ERoute } from 'common/const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { RequestAddGoodsModal } from 'entities/Request/components';
import { getRequestStatus } from 'entities/Request/Request.helper';
import { IRequest } from 'entities/Request/Request.models';
import { ERequestListPageTab } from 'entities/Request/Request.enum';
import { IGood } from 'entities/Goods/Goods.models';
import { EUserRole } from 'entities/User/User.enum';
import { ESupplyStatus } from 'entities/Supply/Supply.enum';
import { getCurrentBasket, selectCurrentBasket, updateBasket } from 'entities/Basket/Basket.slice';
import { cancelRequest, selectIsRequestLoading, uploadRequestExcel } from 'entities/Request/Request.slice';
import { createSupply, selectIsSupplyLoading } from 'entities/Supply/Supply.slice';
import { getUserStats, selectCurrentUser } from 'entities/User/User.slice';
import { SelectGoodCard } from 'entities/Goods/components';
import { getGoodList } from 'entities/Goods/Good.slice';

interface IComponentProps {
  request: IRequest;
  showRequestDetailsToggle: boolean;
  onSearch: (value?: string) => Promise<void>;
  onRequestDetailsClick: (value: boolean) => void;
}

export const RequestHeader: FC<IComponentProps> = ({ request, showRequestDetailsToggle, onSearch, onRequestDetailsClick }) => {
  const dispatch = useAppDispatch();
  // basket
  const currentBasket = useAppSelector(selectCurrentBasket);
  // request
  const isRequestLoading = useAppSelector(selectIsRequestLoading);
  // supply
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [goods, setGoods] = useState<IGoodsShort | null>(null);
  const [openSelectGoodCard, setOpenSelectGoodCard] = useState<boolean>(false);
  const [openRequestAddGoodsModal, setOpenRequestAddGoodsModal] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const navigate = useNavigate();
  const { isSeller } = AuthHooks.useIsSeller();
  const { isManager, isSellerManager } = UserHooks.useCurrentUserRole();

  const isOwner = request.userId === currentUser?.id;

  useEffect(() => {
    if (!currentUser || currentBasket || !isOwner) {
      return;
    }

    dispatch(getCurrentBasket(currentUser.basketId));
  }, [currentBasket, isOwner]);

  const { isCreatedRequest, isProcessingRequest } = getRequestStatus(request.status);
  const suppliesInProcessing = request.supplies.filter((item) => {
    return ![ESupplyStatus.Delivery, ESupplyStatus.Delivered, ESupplyStatus.Cancelled].includes(item.status as ESupplyStatus);
  });
  const canCancelRequest = !isSeller && (isCreatedRequest || isProcessingRequest) && suppliesInProcessing.length;

  const handleOpenSelectGoodCard = async (): Promise<void> => {
    await dispatch(
      getGoodList({
        asUserId: isSellerManager ? request.userId : undefined,
        limit: LIST_LIMIT_20,
        offset: 0,
      }),
    );
    setOpenSelectGoodCard(true);
  };

  const closeSelectGoodCard = (): void => {
    setOpenSelectGoodCard(false);
  };

  const onRequestAddGoodsModalAddClick = (value: number, onSuccess: () => void) => {
    if (goods) {
      dispatch(
        createSupply({
          requestId: request.id,
          supplies: [
            {
              goodId: goods.id,
              count: value,
              price: goods.price,
            },
          ],
          ...(isSellerManager && { asUserId: request.userId }),
          onSuccess: () => {
            setOpenRequestAddGoodsModal(false);
            setOpenSelectGoodCard(false);
            onSuccess();
          },
        }),
      );
    }
  };

  const handleRepeatRequest = (): void => {
    if (!currentBasket) {
      return;
    }

    const basketGoods = currentBasket.goods.map((item) => {
      return {
        goodId: item.goodId,
        count: item.count,
      };
    });
    const suppliesGoods = request.supplies.map((item) => {
      return {
        goodId: item.good.id,
        count: item.count,
      };
    });
    const uniqueGoods = Object.values(
      [...basketGoods, ...suppliesGoods].reduce<Record<number, { goodId: number; count: number }>>((acc, item) => {
        acc[item.goodId] ??= { goodId: item.goodId, count: 0 };
        acc[item.goodId].count += item.count;
        return acc;
      }, {}),
    );

    dispatch(
      updateBasket({
        id: currentBasket.id,
        goods: uniqueGoods,
        onSuccess: () => {
          showSuccessMessage('Товары из заказа успешно добавлены в корзину.');
          dispatch(getUserStats());
        },
      }),
    );
  };

  const showWarningCard = () => {
    setOpenWarningCard(true);
  };

  const closeWarningCard = () => {
    setOpenWarningCard(false);
  };

  const handleCancelRequest = () => {
    dispatch(
      cancelRequest({
        id: request.id,
        onSuccess: () => {
          setOpenWarningCard(false);

          if (
            !request.supplies.some((item) => item.status === ESupplyStatus.Delivery || item.status === ESupplyStatus.Delivered)
          ) {
            navigate(`${ERoute.RequestList}/${isManager ? EUserRole.Manager : EUserRole.User}/${ERequestListPageTab.Requests}`);
          }
        },
      }),
    );
  };

  return (
    <>
      <div className="request-header">
        <BackBtn />

        <div className="request-header__container">
          <div className="flex-1" style={{ maxWidth: 472 }}>
            <SearchInput placeholder="Поиск по заказу" minLength={DEFAULT_VALUE_0} clearDeps={[request.id]} onSearch={onSearch} />
          </div>

          <Dropdown
            overlayClassName="redesign"
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{ width: 360 }}
            menu={{
              items: [
                {
                  key: 'download',
                  label: (
                    <div className="flex align-center">
                      <Download className="mr-3" />
                      <span className="ff-regular fs-s">Экспорт в Excel</span>
                    </div>
                  ),
                  onClick: () => dispatch(uploadRequestExcel(request.id)),
                },
                ...(!isSeller && isOwner
                  ? [
                      {
                        key: 'repeat',
                        label: (
                          <div>
                            <div className="mb-3 flex align-center">
                              <Sync className="mr-3" />
                              <span className="ff-regular fs-s">Повторить заказ</span>
                            </div>
                            <span className="ff-regular fs-xs color-dark-grey">Товары из заказа появятся в вашей корзине</span>
                          </div>
                        ),
                        onClick: handleRepeatRequest,
                      },
                    ]
                  : []),
                ...(canCancelRequest
                  ? [
                      {
                        type: 'divider' as any,
                      },
                      {
                        key: 'cancel',
                        label: (
                          <div>
                            <div className="mb-3 flex align-center">
                              <Bin className="mr-3 icon-bin-pale-red" />
                              <span className="ff-regular fs-s color-red">Отменить заказ</span>
                            </div>
                            <span className="ff-regular fs-xs color-dark-grey">
                              Отменить все товары, кроме “В пути” / “Доставлено”
                            </span>
                          </div>
                        ),
                        onClick: showWarningCard,
                      },
                    ]
                  : []),
              ],
            }}
          >
            <Button className="button-circle secondary" icon={<Options />} />
          </Dropdown>

          {isSellerManager && isProcessingRequest && (
            <Tooltip overlayClassName="redesign" title="Быстрое добавление товара">
              <Button
                className="button-circle primary"
                icon={<Plus className="icon-plus-white" />}
                onClick={handleOpenSelectGoodCard}
              />
            </Tooltip>
          )}

          {showRequestDetailsToggle && (
            <Button className="button-sm secondary" icon={<Expand />} onClick={() => onRequestDetailsClick(true)}>
              Детали заказа
            </Button>
          )}
        </div>
      </div>

      <SelectGoodCard
        open={openSelectGoodCard}
        asUserId={isSellerManager ? request.userId : undefined}
        onCancel={closeSelectGoodCard}
        onSelect={(value: IGood) => {
          setGoods(value);
          setOpenRequestAddGoodsModal(true);
        }}
      />

      <RequestAddGoodsModal
        open={openRequestAddGoodsModal}
        goods={goods}
        loading={isSupplyLoading}
        onClose={() => setOpenRequestAddGoodsModal(false)}
        onAdd={onRequestAddGoodsModalAddClick}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите отменить заказ?"
        subtitle="Это действие необратимо. Чтобы приобрести товары снова, вы можете повторить заказ."
        confirmBtnTitle="Отменить"
        cancelBtnTitle="Не отменять"
        loading={isRequestLoading}
        onClose={closeWarningCard}
        onConfirm={handleCancelRequest}
      />
    </>
  );
};
